
/**
 * Idle — referans-parite (features/ui/state/Idle.ts) 1-1 port.
 *
 * Kullanıcının KENDİ etkinlik/boşta (idle) durumunu takip eden client-side mobx
 * singleton'ı. `recordActivity()` her kullanıcı etkileşiminde çağrılır; belirli
 * süre (IDLE_DURATION_MS) etkinlik olmazsa `idle=true` olur ve LocalPresence'a
 * presence'ı yeniden hesaplatır. Desktop (Electron) ortamında sistem-idle API'si
 * varsa onu kullanır; yoksa (web/rspack) yerel etkinlik zaman damgasına düşer.
 *
 * kaynak-referanstan fark YOK — mantık birebir. Web ortamında `window.electron` bulunmadığı
 * için sistem-idle yolu otomatik devre dışı kalır (native ZORLAMA yok, web-safe).
 */

import LocalPresence from '@app/features/presence/state/LocalPresence';
import {makeAutoObservable} from 'mobx';

// DURUM-DÜZELTMESİ (2026-07-27): eskiden `IS_DEV ? 10 : 60 * 10` idi — dev'de
// 10 SANİYE sonra herkes "Boşta"ya düşüyordu. Süre artık her iki ortamda da
// Discord paritesiyle 10 dakika (ortam ayrımı yok).
const IDLE_DURATION_MS = 1000 * 60 * 10;
const IDLE_CHECK_INTERVAL_MS = Math.min(Math.floor(IDLE_DURATION_MS * 0.25), 30_000);
const SYSTEM_IDLE_RETRY_DELAY_MS = 60_000;

/**
 * DOM etkinlik olayları — `recordActivity()`'yi besleyen tek kaynak. Bu köprü
 * OLMADAN `lastActivityTime` modül yüklenme anında donuyordu ve kullanıcı
 * IDLE_DURATION_MS sonra KALICI olarak "Boşta" yayınlanıyordu (seçtiği status
 * "Çevrimiçi" olsa bile) — bildirilen "çevrimiçi seçiyorum ama karşı taraf
 * boşta görüyor" hatasının kök nedeni.
 */
const ACTIVITY_EVENTS = [
	'mousemove',
	'mousedown',
	'keydown',
	'wheel',
	'touchstart',
	'touchmove',
	'scroll',
	'pointerdown',
] as const;

/** Etkinlik kaydını en fazla bu sıklıkta işle (mobx yazımını kısıtla). */
const ACTIVITY_THROTTLE_MS = 1_000;

interface DesktopIdleApi {
	getSystemIdleTimeMs?: () => Promise<number>;
}

function getDesktopIdleApi(): Required<DesktopIdleApi> | null {
	// Node/test ortamı guard'ı: window yoksa desktop idle API'si de yoktur
	// (modül-seviyesi interval test runner'da da tetiklenir).
	if (typeof window === 'undefined') return null;
	const electronApi = (
		window as {
			electron?: DesktopIdleApi | null;
		}
	).electron;
	return typeof electronApi?.getSystemIdleTimeMs === 'function'
		? {getSystemIdleTimeMs: electronApi.getSystemIdleTimeMs.bind(electronApi)}
		: null;
}

function normalizeIdleTimeMs(value: number): number | null {
	if (!Number.isFinite(value) || value < 0) return null;
	return Math.floor(value);
}

class Idle {
	idle = false;
	private lastActivityTime = Date.now();
	private checkInterval: ReturnType<typeof setInterval> | null = null;
	private systemIdleCheckInFlight = false;
	private lastSystemIdleFailureAt = 0;
	private activityVersion = 0;
	private activityListenersInstalled = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.startIdleCheck();
		this.installActivityListeners();
	}

	/**
	 * Gerçek kullanıcı etkinliğini `recordActivity()`'ye bağlar. Web'de tek
	 * yetkili etkinlik sinyali budur; Electron'da sistem-idle API'si zaten
	 * `updateIdleStateFromSystem` üzerinden devreye girer ve bu köprü yalnızca
	 * ek doğruluk sağlar (ikisi çakışmaz — sistem yolu lastActivityTime'ı
	 * kendisi yazar ve activityVersion ile yarış korumalıdır).
	 */
	private installActivityListeners(): void {
		if (this.activityListenersInstalled) return;
		if (typeof window === 'undefined' || typeof document === 'undefined') return;
		this.activityListenersInstalled = true;

		// Throttle sayacı closure-local: mobx observable'ı olmamalı (her mousemove'da
		// action dışı yazım uyarısı üretirdi).
		let lastRecordedAt = 0;
		const onActivity = () => {
			const now = Date.now();
			// mousemove saniyede yüzlerce kez tetiklenir; idle DEĞİLKEN her birini
			// mobx'e yazmak gereksiz. Idle İSE gecikmesiz uyandır.
			if (!this.idle && now - lastRecordedAt < ACTIVITY_THROTTLE_MS) return;
			lastRecordedAt = now;
			this.recordActivity();
		};

		for (const eventName of ACTIVITY_EVENTS) {
			window.addEventListener(eventName, onActivity, {passive: true, capture: true});
		}
		window.addEventListener('focus', onActivity);
		document.addEventListener('visibilitychange', () => {
			// Sekme tekrar görünür olduğunda kullanıcı geri döndü sayılır.
			if (!document.hidden) onActivity();
		});
	}

	private startIdleCheck(): void {
		if (typeof setInterval !== 'function') return;
		this.checkInterval = setInterval(() => {
			this.updateIdleState();
		}, IDLE_CHECK_INTERVAL_MS);
	}

	destroy(): void {
		if (this.checkInterval !== null) {
			clearInterval(this.checkInterval);
			this.checkInterval = null;
		}
	}

	recordActivity(): void {
		this.lastActivityTime = Date.now();
		this.activityVersion++;
		if (this.idle) {
			this.applyIdleState(false);
		}
	}

	markBackground(): void {
		this.lastActivityTime = 0;
		this.activityVersion++;
		this.applyIdleState(true);
	}

	isIdle(): boolean {
		return this.idle;
	}

	getIdleSince(): number {
		return this.idle ? this.lastActivityTime : 0;
	}

	getInactiveDurationMs(now = Date.now()): number {
		return Math.max(0, now - this.lastActivityTime);
	}

	private updateIdleState(): void {
		const desktopIdleApi = getDesktopIdleApi();
		if (desktopIdleApi && Date.now() - this.lastSystemIdleFailureAt >= SYSTEM_IDLE_RETRY_DELAY_MS) {
			void this.updateIdleStateFromSystem(desktopIdleApi);
			return;
		}
		this.updateIdleStateFromLocalActivity();
	}

	private updateIdleStateFromLocalActivity(): void {
		const now = Date.now();
		this.applyIdleState(this.getInactiveDurationMs(now) >= IDLE_DURATION_MS);
	}

	private async updateIdleStateFromSystem(desktopIdleApi: Required<DesktopIdleApi>): Promise<void> {
		if (this.systemIdleCheckInFlight) return;
		this.systemIdleCheckInFlight = true;
		const activityVersion = this.activityVersion;
		const requestedAt = Date.now();
		try {
			const idleTimeMs = normalizeIdleTimeMs(await desktopIdleApi.getSystemIdleTimeMs());
			if (idleTimeMs === null) {
				this.lastSystemIdleFailureAt = Date.now();
				this.updateIdleStateFromLocalActivity();
				return;
			}
			if (activityVersion !== this.activityVersion && this.lastActivityTime >= requestedAt) {
				return;
			}
			const now = Date.now();
			this.lastActivityTime = Math.max(0, now - idleTimeMs);
			this.applyIdleState(idleTimeMs >= IDLE_DURATION_MS);
		} catch {
			this.lastSystemIdleFailureAt = Date.now();
			this.updateIdleStateFromLocalActivity();
		} finally {
			this.systemIdleCheckInFlight = false;
		}
	}

	private applyIdleState(idle: boolean): void {
		if (idle !== this.idle) {
			this.idle = idle;
			LocalPresence.updatePresence();
		}
	}
}

export default new Idle();
